import type { UiText } from '@uplink/ui';
import type { Messages } from './ru.ts';

const s = (n: number, one: string, many: string) => (n === 1 ? one : many);

const ui: UiText = {
  balance: 'Balance',
  history: 'History',
  monthlySpend: 'Monthly spend',
  runway: 'Lasts until',
  topUp: 'Top up',
  copy: 'Copy',
  copied: 'Copied',
  sections: 'Sections',
  amount: 'Amount',
  domainAvailable: 'Available',
  domainTaken: 'Taken',
  domainPremium: 'Premium',
  priceOnRequest: 'on request',
  renewPerYear: (price) => `renews at ${price} / year`,
  serverState: {
    ok: 'Running',
    busy: 'High load',
    error: 'Unreachable',
    off: 'Off',
    provisioning: 'Creating',
  },
};

export const en: Messages = {
  locale: 'en-US',
  ui,
  common: {
    back: 'Back',
    show: 'Show',
    hide: 'Hide',
    copy: 'Copy',
    copied: 'Copied',
    delete: 'Delete',
    change: 'Change',
    cancel: 'Cancel',
    save: 'Save',
    continue: 'Continue',
    done: 'Done',
    all: 'All',
    allN: (n) => `All ${n}`,
    perMonth: 'per month',
    perYear: 'per year',
    from: (price) => `from ${price}`,
    topUpBy: (amount) => `Top up ${amount}`,
    autoRenew: 'Auto-renewal',
    until: (date) => `until ${date}`,
  },
  errors: {
    fields: 'Check the highlighted fields',
    openInTelegram: 'Open the app from Telegram',
    signedOut: 'You are signed out. Reload the page and sign in again',
    network: "Couldn't reach the server. Check your connection and try again",
  },
  tabs: { home: 'Home', domains: 'Domains', servers: 'Servers', balance: 'Balance', profile: 'Profile' },

  home: {
    greeting: (name) => `Hi, ${name}`,
    payVia: (names) => `Crypto via ${names.join(' or ')}`,
    domain: 'Domain',
    domainFrom: (price) => `from ${price} a year`,
    domainHint: 'Search and register',
    server: 'Server',
    serverFrom: (price) => `from ${price} a month`,
    serverHint: 'Cloud servers',
    myServers: 'Servers',
    myDomains: 'Domains',
    emptyTitle: 'No services yet',
    emptyText: 'Your servers and domains will show up here: status, IP, renewal date.',
  },

  trialVpn: {
    title: 'Trial VPN',
    promo: '24 hours free',
    activePromo: 'Your key is ready',
    intro: 'Get a personal VLESS key, connect, and test Uplink speed on your own device.',
    terms: 'One trial per account, up to 2 devices and 10 GB of traffic.',
    issue: 'Get a key for 24 hours',
    key: 'Your VLESS key',
    validUntil: (date) => `Valid until ${date}`,
    check: 'Connect and check VPN',
    expired: 'Trial period ended',
    expiredText: 'The free key has already been used on this account.',
  },

  sharedVpn: {
    shortTitle: 'VPN access',
    from: (price) => `${price} a month`,
    title: 'VPN subscription',
    intro: 'A ready-to-use VPN key without buying or configuring a server. Connect in a few minutes.',
    key: 'Your VLESS key',
    validUntil: (date) => `Paid until ${date}`,
    autoRenew: 'Auto-renewal',
    price: (price) => `${price} every 30 days`,
    devices: 'Up to 2 devices',
    traffic: '100 GB of traffic for 30 days',
    renewal: 'Auto-renewal can be turned off',
    ownTitle: 'Need a dedicated server and personal IP?',
    ownText: 'Create your own VPN with a stable IP, control panel and separate keys for family or a team.',
    buy: (price) => `Buy VPN for ${price}`,
    preparing: 'Preparing your VPN key',
    preparingText: 'Payment is reserved; you do not need to pay again. The screen will update when ready.',
  },

  domains: {
    title: 'Domains',
    searchPlaceholder: 'Find a domain',
    zones: 'Zones',
    popular: 'Popular',
    all: 'All',
    checking: 'Checking availability…',
    nothingTitle: 'Nothing found',
    nothingText: 'Try another name or zone.',
    results: 'Results',
    resultsFooter: 'Each zone shows its renewal price. Premium domains are ordered through support.',
    mine: 'My domains',
    expiredRenew: 'Expired, renew it',
    registering: 'Registering',
    autoRenewOn: 'auto-renewal',
    aiRow: 'Pick a name with AI',
    aiRowText: 'From a project description',
    aiLabel: 'What is the project about',
    aiPlaceholder: 'Coffee shop in Berlin with delivery',
    aiHint: 'A few words about the business or idea, any language',
    aiPick: 'Suggest names',
    aiAgain: 'Suggest more',
    aiResults: 'Available names',
    aiFooter: 'Names suggested by AI; availability and price checked with the registrar.',
    aiNothingTitle: 'No free names found',
    aiNothingText: 'Describe the project a little differently or in more detail and try again.',
    emptyTitle: 'Find your domain',
    emptyText:
      "Type a name: we'll check .com, .io, .xyz and other zones and show registration and renewal prices.",
  },

  checkout: {
    domain: 'Domain',
    firstYear: 'for the first year',
    renewal: (price) => `renews at ${price} a year`,
    noRefund: "Registered domains can't be refunded.",
    owner: 'Domain owner',
    ownerUplinkFooter:
      'The domain is registered to Uplink and you manage it right here in the app. Support can move it to another registrar.',
    ownerUplinkSubtitle: 'Your details are not sent to the registrar',
    ownerIcann: 'Your details go to the registrar (an ICANN requirement).',
    ownerIcannSaved:
      "Your details go to the registrar (an ICANN requirement). We'll keep them for your next domains.",
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    emailHint: 'The registry will send a confirmation email here',
    phone: 'Phone',
    country: 'Country',
    countryHint: 'Two-letter code',
    zip: 'Postal code',
    city: 'City',
    street: 'Street and building',
    payment: 'Payment',
    fromBalance: 'From balance',
    buyFor: (price) => `Buy for ${price}`,
    registered: "The domain is registered for a year. We'll renew it from your balance automatically.",
    myDomains: 'My domains',
    registering: (domain) => `Registering ${domain}`,
    manualReview:
      'The registrar is slower than usual, so an operator will check the order. The money is on hold and the bot will report back.',
    usuallyMinute: 'This usually takes under a minute. You can close the app: the bot will message you.',
    refused: (domain) => `The registrar refused ${domain}. The money is back on your balance.`,
    pickAnother: 'Pick another domain',
  },

  domain: {
    renewal: 'Renewal',
    autoFooter:
      "We'll renew from your balance 7 days before expiry. The bot reminds you 30, 7 and 1 day ahead.",
    manualFooter: 'The bot reminds you 30, 7 and 1 day before expiry.',
    validUntil: 'Valid until',
    renewed: 'Renewed. The bot sent a confirmation.',
    point: 'Site on a server',
    pointFooter:
      'The domain and www get A records with the server IP. Mail and other records stay as they are.',
    pointTo: 'Point here',
    pointed: 'Pointed',
    pointDone: (server) => `The domain now points to ${server}. DNS updates in a few minutes.`,
    renewFor: (price) => `Renew for a year for ${price}`,
    statusPending: 'Registering',
    statusExpired: 'Expired',
    statusExpiresIn: (days) => `Expires in ${days} ${s(days, 'day', 'days')}`,
    statusActive: 'Active',
    dnsFailed: (msg) => `DNS didn't load: ${msg}`,
    dnsWhere: 'Where records live',
    dnsUplink: 'Uplink records',
    dnsOwnNs: 'Own NS',
    apex: '@ (the domain itself)',
    deleteRecord: (host) => `Delete record ${host}`,
    noRecords: 'No records yet. Add an A record to point the domain at your server.',
    recordType: 'Record type',
    host: 'Name',
    hostHint: '@ or a subdomain, like www',
    priority: 'Priority',
    value: 'Value',
    valueHint: {
      A: 'Server IPv4 address, like 203.0.113.10',
      AAAA: 'Server IPv6 address',
      CNAME: 'Another host name, like example.com',
      MX: 'Mail server, like mx.zoho.com',
      TXT: 'Text: SPF, ownership checks and so on',
    },
    addRecord: 'Add record',
    switchToUplink: 'The domain uses its own NS now. Saving moves it to Uplink DNS with these records.',
    saveRecords: 'Save records',
    moreNs: 'Another NS',
    nsFooter: 'For example, Cloudflare or your hosting NS. Uplink records will stop working.',
    saveNs: 'Save NS',
    transfer: 'Transfer',
    transferFooter: 'You need this code to move the domain to another registrar. Never share it.',
    authCode: 'Transfer code',
  },

  servers: {
    title: 'Servers',
    emptyTitle: 'No servers yet',
    emptyText: (from) =>
      `A cloud server with root access in a couple of minutes: Europe, the Americas and Asia${from ? `, from ${from} a month` : ''}.`,
    create: 'Create server',
    createVpn: 'Own VPN',
    regions: { europe: 'Europe', americas: 'Americas', asia: 'Asia', cis: 'CIS' },
    state: {
      creating: 'Creating',
      failed: 'Creation failed',
      suspended: 'Stopped for non-payment',
      deleted: 'Deleted',
      running: 'Running',
      stopped: 'Off',
      reinstalling: 'Reinstalling',
      blocked: 'Blocked',
      busy: 'Working on it',
    },
    gb: 'GB',
    mb: 'MB',
    disk: (gb) => `${gb} GB disk`,
  },

  serverCreate: {
    vpnUnavailable: 'VPN servers are unavailable right now. Check back later or contact support',
    title: 'New server',
    purpose: 'What the server is for',
    purposeServer: 'Server',
    purposeVpn: 'Own VPN',
    vpnWhich: 'Which VPN to install',
    vpnShort: 'VPN',
    vpnFooter:
      "We install the VPN on the server's first start and send you the key. It takes 3–10 minutes after the server is ready.",
    vpnBenefitsTitle: 'Why choose your own VPN',
    vpnBenefits: [
      'A separate server and stable IP just for you',
      'Your own panel and separate keys for family or team members',
      'Unrelated VPN users cannot consume your server resources',
    ],
    trialButton: 'Try the VPN — 24 hours free',
    loadingPlans: 'Plans are still loading. Check back in a couple of minutes.',
    where: 'Where to host',
    whereShort: 'Where',
    region: 'Region',
    city: 'City',
    power: 'Size',
    plan: 'Configuration',
    allPlans: (n) => `All configurations (${n})`,
    system: 'System',
    vpnLaterRequiresUbuntu:
      'If you want to install a VPN through Uplink later, you will need Ubuntu 24.04 LTS.',
    version: 'Version',
    details: 'Details',
    name: 'Name',
    namePlaceholder: 'For example, web or vpn',
    nameHint: 'Only you see it',
    addSsh: 'Add an SSH key',
    ssh: 'Public SSH key',
    sshHint: 'The contents of ~/.ssh/id_ed25519.pub. The root password will be in the app too',
    sshPick: 'SSH key',
    sshNone: 'No key',
    sshNoneHint: 'Sign in with the root password',
    sshNew: 'New key',
    sshSave: 'Save the key to my profile',
    monthly: 'Per month',
    onBalance: 'On balance',
    billingFooter:
      'Charged from your balance every 30 days; you can turn auto-renewal off. Delete the server any time; the rest of the month is not refunded.',
    createFor: (price) => `Create for ${price} a month`,
  },

  vpn: {
    title: 'VPN',
    recommended: 'best for Russia',
    which: 'Which VPN',
    about: {
      vless: 'Looks like ordinary HTTPS and works reliably in Russia. 3X-UI panel for more keys',
      amneziawg: 'Fast WireGuard masked against blocking. AmneziaVPN app',
      outline: 'The simplest: one key and the Outline app. Sometimes blocked in Russia',
      wireguard: 'Classic WireGuard with a web panel. Good outside Russia',
    },
    intro: 'Turn this server into your own VPN: one command installs it and you get a key to connect.',
    installAutomatic: 'Install VPN',
    getCommand: 'Get the install command',
    retry: 'Get the reinstall command',
    retryAutomatic: 'Try again',
    installManually: 'Install manually',
    hideManual: 'Hide manual installation',
    commandSteps: [
      'Connect to the server over SSH, as in the guide above',
      'Paste the command and press Enter',
      'Wait 3–10 minutes: the key appears here and the bot sends a message',
    ],
    commandNote: 'The command works once and only for this server.',
    steps: ['Server is starting', 'Installing packages', 'Installing the VPN', 'Creating the key'],
    installingFooter:
      'Usually 3–10 minutes. You can close the app: the bot will message you when the VPN is ready.',
    failed: (error) =>
      `The VPN didn't install${error ? `: ${error}` : ''}. The server works; retry automatically or install it manually.`,
    failedTimeout:
      'The installer did not report back. Try the automatic installation again or install the VPN manually.',
    failedUnsupported:
      'This server runs an unsupported system. Standard VPN installation is available only on Ubuntu 24.04 LTS; reinstall the server with it.',
    reinstallForVpn: (image) => `Reinstall with ${image} for VPN`,
    reinstallConfirm: 'All data will be erased. Tap again',
    reinstallUnavailable:
      'This provider cannot yet be reinstalled safely through Uplink. Create a new server with Ubuntu 24.04 LTS.',
    ready: 'ready',
    qr: 'VPN key QR code',
    scanLink: 'Scan the QR code in the app, or copy the key and add it from the clipboard.',
    scanConfig: 'Scan the QR code in the app, or copy the config and import it.',
    howTo: 'Apps to connect with',
    clientPlatform: 'Your device',
    clientPlatforms: {
      apple: 'Apple',
      android: 'Android',
      windows: 'Windows',
      linux: 'Linux',
    },
    install: 'Install',
    appGuideTitle: (name) => `How to connect with ${name}`,
    appGuideInstall: (name) => `1. Install ${name}`,
    appGuideInstallHint:
      'If the app is already installed, continue to the next step. If the store opens inside Telegram, choose "⋯" → "Open in Browser".',
    appGuideCopy: '2. Copy the VPN key',
    appGuideCopyHint: 'Tap Copy. The complete key is saved to your clipboard.',
    appGuideImport: (name) => `3. Add the key to ${name}`,
    appGuideSteps: {
      v2box: [
        'Open the Configs tab at the bottom.',
        'Tap "+" in the top-right corner.',
        'Choose Import V2Ray URI from Clipboard.',
        'Return to Home, select the added connection, and tap Tap to Connect.',
      ],
      v2raytun: [
        'Open the app and tap "+".',
        'Choose to import a configuration from the clipboard.',
        'Confirm adding the server.',
        'Select the added server and tap the connect button.',
      ],
      happ: [
        'Open the app and tap "+" in the top-right corner.',
        'Choose to add a configuration from the clipboard or URL.',
        'Confirm importing the VLESS configuration.',
        'Select the added server and turn on the VPN.',
      ],
      hiddify: [
        'Open the app and tap "+" in the top-right corner.',
        'Choose Add from clipboard.',
        'Confirm adding the profile.',
        'Select the profile and tap the large connect button.',
      ],
      v2rayn: [
        'Extract the downloaded archive and run v2rayN.exe.',
        'Open v2rayN from the Windows notification area.',
        'Choose Servers → Import share links from clipboard.',
        'Select the added server and enable the system proxy.',
      ],
      v2raya: [
        'Install v2rayA using the instructions for your Linux distribution.',
        'Open the v2rayA panel at http://127.0.0.1:2017.',
        'Import the copied VLESS link.',
        'Select the added server and start it.',
      ],
    },
    backToApps: 'Back to app selection',
    howToFooter: {
      vless: 'Choose an app to open detailed installation and connection instructions.',
      amneziawg: 'In AmneziaVPN, tap "+", then "File with settings" or a QR code.',
      outline: 'Open Outline, tap "+" and paste the key.',
      wireguard: 'In WireGuard, tap "+" and choose "Scan from QR code" or import from the clipboard.',
    },
    panel: 'Control panel',
    panelFooter: {
      vless:
        'Open the address in a browser: in the 3X-UI panel you can add keys for friends and watch traffic.',
      amneziawg: '',
      outline: '',
      wireguard: 'Open the address in a browser: in the wg-easy panel you can add keys for other devices.',
    },
    panelCertificateWarning:
      'The panel uses a temporary certificate: the browser will show a security warning the first time you open it.',
    panelMissing: 'Panel credentials were not saved during installation',
    panelMissingHint:
      'The VPN keeps working. To get the panel, tap “Install another VPN” and choose VLESS Reality again.',
    login: 'Login',
    password: 'Password',
    manager: 'Outline Manager',
    managerFooter: 'To add keys, install Outline Manager, choose "Add server" and paste this line.',
    another: 'Install another VPN',
  },

  server: {
    name: 'Server name',
    namePlaceholder: 'For example, Family VPN',
    creating: 'Creating the server',
    stepPaid: 'Paid',
    stepCreate: 'Creating the server',
    stepInstall: (image) => `Installing ${image}`,
    stepVpn: 'Installing VPN',
    stepReady: 'Ready',
    creatingFooter: 'Usually 1–3 minutes. You can close the app: the bot will tell you when it is ready.',
    access: 'Access',
    accessFooter: 'Only you can see the password. If someone else got it, reset it.',
    monitoring: 'Monitoring',
    monitoringIntro:
      'CPU, memory, disk and network charts. Run one command on the server once, and if it stops responding the bot tells you right away.',
    monitoringConnect: 'Turn on monitoring',
    monitoringSteps: [
      'Connect to the server over SSH (see above).',
      'Paste the command and press Enter.',
      'Charts appear here within a minute.',
    ],
    monitoringWaiting: 'Waiting for the first data from the server…',
    monitoringShowCommand: 'Show the install command',
    monitoringOnline: 'reporting',
    monitoringOffline: (when) => `not responding since ${when}`,
    monitoringRemove: 'How to turn monitoring off',
    monitoringRemoveHint: 'Run this on the server to remove the agent:',
    monitoringNote: 'The agent only reads the load and sends numbers; it runs no commands from outside.',
    cpu: 'CPU',
    ram: 'Memory',
    disk: 'Disk',
    net: 'Network',
    netIn: 'in',
    netOut: 'out',
    ranges: { '1h': '1 hour', '24h': '24 hours', '7d': '7 days' },
    bytes: ['B', 'KB', 'MB', 'GB', 'TB'],
    perSecond: '/s',
    readyTitle: 'Your server is ready',
    readyText: 'Connect over SSH: the command and the password are below, with step-by-step instructions.',
    failedTitle: 'Creation failed',
    failedText: 'The provider did not create the server. Its response is shown below.',
    refunded: 'The full amount has been returned to your balance.',
    guide: 'How to connect',
    guideFooter: "The password stays invisible while you type, that's expected. Paste it and press Enter.",
    guideOs: { windows: 'Windows', mac: 'macOS', linux: 'Linux', phone: 'Phone' },
    guideSteps: {
      windows: [
        'Press Win + X and choose "Terminal" or "PowerShell".',
        'Paste the connect command and press Enter.',
        'When asked "Are you sure you want to continue connecting", type yes.',
        'Paste the root password and press Enter.',
      ],
      mac: [
        'Press Cmd + Space, type "Terminal" and open it.',
        'Paste the connect command and press Enter.',
        'When asked "Are you sure you want to continue connecting", type yes.',
        'Paste the root password and press Enter.',
      ],
      linux: [
        'Open a terminal (usually Ctrl + Alt + T).',
        'Paste the connect command and press Enter.',
        'When asked "Are you sure you want to continue connecting", type yes.',
        'Paste the root password and press Enter.',
      ],
      phone: [
        'Install the free Termius app from the App Store or Google Play.',
        'Add a host: address is the server IP, user is root.',
        'Paste the root password and connect.',
      ],
    },
    connect: 'Connect',
    user: 'User',
    rootPassword: 'Root password',
    resetPassword: 'Reset password',
    passwordReset: 'New password is ready, tap Show',
    power: 'Power',
    reboot: 'Reboot',
    start: 'Turn on',
    shutdown: 'Shut down',
    billing: 'Billing',
    suspendedFooter:
      'The server is stopped and its data is kept for now. Pay for a month within 3 days or the server will be deleted.',
    autoFooter:
      "We'll renew from your balance on the due date. The bot warns you 3 days and 1 day ahead if funds are short.",
    manualFooter: 'Auto-renewal is off: renew by hand or the server will stop.',
    paidUntil: 'Paid until',
    perMonth: (price) => `${price} a month`,
    renewShort: (amount) => `Top up ${amount} to renew`,
    renewFor: (price) => `Renew for a month for ${price}`,
    reinstall: 'Reinstall the system',
    reinstallTo: (image) => `Reinstall with ${image}`,
    reinstallConfirm: 'All data will be erased. Tap again',
    danger: 'Deletion',
    delete: 'Delete server',
    deleteConfirm: 'The server and its data will be gone for good. Tap again',
    deleteFooter: 'The rest of the paid month is not refunded.',
    payAndStart: (price) => `Pay ${price} and start`,
  },

  balance: {
    title: 'Balance',
    emptyTitle: 'No transactions yet',
    emptyText: 'Top-ups and service charges will show up here.',
    history: 'History',
    inUsd: (usd) => `${usd} in the account`,
    promo: 'Promo code',
    promoRow: 'Enter a promo code',
    promoPlaceholder: 'For example, WELCOME',
    promoApply: 'Apply',
    promoDone: (amount) => `Promo code applied: +${amount} to your balance`,
    runwayUntil: (date) => `until ${date}`,
    runwayYear: 'over a year',
  },

  topup: {
    title: 'Top up',
    amount: 'Amount',
    range: (min, max) => `Amount from ${min} to ${max}`,
    payment: 'Payment method',
    footer: (amount, via) =>
      `The ${via} invoice is valid for 30 minutes. ${via} converts to crypto at the current rate; your balance gets exactly ${amount ?? 'the amount entered'}.`,
    credit: 'Credited to balance',
    approx: (value) => `≈ ${value}`,
    pay: (amount, via) => `Pay ${amount} in ${via}`,
    enterAmount: 'Enter an amount',
    paid: (asset) => `Balance topped up${asset ? `, paid in ${asset}` : ''}`,
    backToOrder: 'Back to the order',
    expired: 'Invoice expired',
    waiting: (via) => `Waiting for payment in ${via}`,
    expiredText: 'Create a new invoice. If you already paid, the money will still reach your balance.',
    waitingText: (amount) =>
      `Pay ${amount} in the window that opened. The balance updates by itself and the bot will message you.`,
    newInvoice: 'Create a new invoice',
    reopen: 'Open the invoice again',
    firstBonus: (pct, max) => `+${pct}% on your first top-up, up to ${max}`,
    resuming: (what) => `Placing the order: ${what}`,
    resumingText: 'Your balance is topped up, the order goes through by itself.',
    resumeFailed: "Your balance is topped up but the order didn't go through. Go back to it and try again.",
  },

  gift: {
    title: 'Gift',
    what: 'What to give',
    amount: 'An amount',
    domain: 'A domain',
    domainLabel: 'Domain for your friend',
    domainHint: "We'll check it's free and show the first-year price",
    domainFree: (price) => `Available, ${price} for the first year`,
    domainTaken: 'Already taken, try another',
    message: 'A note',
    messagePlaceholder: 'Happy birthday! May your site take off',
    footer:
      'The money leaves your balance and reaches your friend when they open the link. Until then you can cancel.',
    give: (amount) => `Give for ${amount}`,
    pick: 'Pick an amount or a domain',
    short: (amount) => `Top up ${amount}`,
    ready: 'The gift is ready',
    readyText: 'Send the link to a friend: the gift can be taken once.',
    share: 'Send to a friend',
    shareText: 'A gift for you in Uplink 🎁',
    sent: 'Sent',
    statusActive: 'waiting',
    statusClaimed: (name) => (name ? `taken by ${name}` : 'taken'),
    statusCancelled: 'cancelled',
    cancel: 'Cancel',
    another: 'Give another',
  },

  desk: {
    subtitle: 'Your balance, servers and domains in one place',
    history: 'History',
    topUp: 'Top up',
    balance: 'Balance',
    monthly: 'Monthly spend',
    runway: 'Lasts',
    create: 'Create',
    vpnSub: 'Key and QR code in 5–10 minutes',
    gift: 'Gift',
    giftSub: 'An amount or a domain for a friend',
    noServers: 'No servers yet. Create one: it is ready in a couple of minutes.',
    noDomains: 'No domains yet. Find a name and buy it in a minute.',
    findDomain: 'Find a domain',
    autoOn: 'auto-renew',
    autoOff: 'manual',
    signOut: 'Sign out',
  },

  web: {
    sections: 'Sections',
    openTelegram: 'Open in Telegram',
    linkTelegram: 'Link Telegram',
    linkingTelegram: 'Confirm in Telegram…',
    telegramLinked: (username) => (username ? `Telegram · @${username}` : 'Telegram linked'),
    telegramNotLinked: 'Telegram not linked',
  },

  account: {
    title: 'Account',
    confirmed: 'confirmed',
    unconfirmed: 'Email not confirmed',
    resend: 'Resend',
    sent: 'Sent',
    webSignIn: 'Website sign-in',
    webSignInText: 'Email and password for uplinkcloud.io',
    webFooter: 'Set an email and password to use the same account in a browser.',
    telegramFooter:
      'Link Telegram: the bot opens, confirm with a button. If Telegram already had an Uplink account, its balance, domains and servers join this one.',
    password: 'Change password',
    telegram: 'Telegram',
    linked: 'linked',
    linkTelegram: 'Link Telegram',
    linkWaiting: 'Confirm the link in the bot…',
    email: 'Email',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordHint: 'At least 8 characters',
    save: 'Save',
    signOut: 'Sign out',
  },

  profile: {
    title: 'Profile',
    settings: 'Settings',
    language: 'Language',
    currency: 'Currency',
    theme: 'Theme',
    themeAuto: 'Auto',
    themeLight: 'Light',
    themeDark: 'Dark',
    currencyFooter:
      'Balance, top-ups and charges are in US dollars. Rubles and euros use the Central Bank of Russia rate.',
    noRates: 'Exchange rates are unavailable right now, so prices are in dollars.',
    support: 'Support',
    supportRow: 'Message support',
    supportFooter: 'Write to the bot in the chat; an operator replies there.',
    invite: 'Invite friends',
    inviteTitle: (pct) => `${pct}% of every friend's top-up`,
    inviteTitleNoReward: 'Your link',
    inviteText:
      'A friend joins through your link, and a share of every top-up they make lands on your balance. For good.',
    inviteShare: 'Share',
    inviteShareText: 'Domains and servers right in Telegram, paid with crypto',
    invited: 'Invited',
    earned: 'Earned',
    giftRow: 'Gift a domain or an amount',
    sshKeys: 'SSH keys',
    sshKeysFooter: 'Pick a saved key when creating a server instead of pasting it again.',
    sshAdd: 'Add a key',
    sshAddDone: 'Key saved',
    sshDelete: 'Delete',
    sshPlaceholder: 'ssh-ed25519 AAAA… user@laptop',
  },
};
